import React, { useState, useEffect } from 'react';
import { getSymbols } from '../api';

const BacktestForm = ({ formData, handleInputChange, runBacktest, isRunning, theme, isDarkTheme }) => {
  const [symbols, setSymbols] = useState([]);
  const [loadingSymbols, setLoadingSymbols] = useState(true);

  useEffect(() => {
    // Fetch available symbols on component mount
    const fetchSymbols = async () => {
      try {
        const data = await getSymbols();
        setSymbols(data);
      } catch (error) {
        console.error('Error fetching symbols:', error);
      } finally {
        setLoadingSymbols(false);
      }
    };

    fetchSymbols();
  }, []);

  // Shrink form: reduce paddings, font sizes, margins
  const formGroupStyle = { marginBottom: '12px' };
  const labelStyle = { 
    display: 'block', 
    fontSize: '12px', 
    color: theme?.textSecondary || '#666', 
    marginBottom: '4px',
    fontWeight: 500
  };
  const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${theme?.border || '#ddd'}`,
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    backgroundColor: theme?.cardBg || 'white',
    color: theme?.textPrimary || '#333'
  };

  const spinnerStyle = {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite'
  };

  // Date validation logic
  const today = new Date().toISOString().split('T')[0];
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    // Validate dates whenever they change
    if (formData.startDate > today) {
      setDateError('Start date cannot be in the future.');
    } else if (formData.endDate > today) {
      setDateError('End date cannot be in the future.');
    } else if (formData.endDate <= formData.startDate) {
      setDateError('End date must be after start date.');
    } else {
      setDateError('');
    }
  }, [formData.startDate, formData.endDate, today]);

  return (
    <div style={{ maxWidth: 320, margin: '0 auto' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 500, marginBottom: '14px', textAlign: 'center' }}>
        Backtesting Parameters
      </h2>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Symbol</label>
        {loadingSymbols ? (
          <input
            type="text"
            name="symbol"
            value={formData.symbol}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="Loading symbols..."
            disabled
          />
        ) : symbols.length > 0 ? (
          <select
            name="symbol"
            value={formData.symbol}
            onChange={handleInputChange}
            style={inputStyle}
          >
            {symbols.map((sym) => (
              <option key={sym} value={sym}>
                {sym}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            name="symbol"
            value={formData.symbol}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="Enter symbol (e.g., RELIANCE)"
          />
        )}
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Commission (Trade %)</label>
        <input
          type="number"
          name="commission"
          value={formData.commission}
          onChange={handleInputChange}
          step="0.0001"
          style={inputStyle}
        />
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Timeframe</label>
        <select
          name="timeframe"
          value={formData.timeframe}
          onChange={handleInputChange}
          style={inputStyle}
        >
          <option value="5m">5 Minutes</option>
          <option value="15m">15 Minutes</option>
          <option value="30m">30 Minutes</option>
          <option value="1h">1 Hour</option>
          <option value="1d">1 Day</option>
        </select>
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Start Date</label>
        <input
          type="date"
          name="startDate"
          value={formData.startDate}
          onChange={handleInputChange}
          style={inputStyle}
          max={today}
        />
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>End Date</label>
        <input
          type="date"
          name="endDate"
          value={formData.endDate}
          onChange={handleInputChange}
          style={inputStyle}
          min={formData.startDate}
          max={today}
        />
      </div>

      {dateError && (
        <div style={{ color: '#d93025', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>{dateError}</div>
      )}

      <div style={formGroupStyle}>
        <label style={labelStyle}>Starting Equity</label>
        <input
          type="number"
          name="startingEquity"
          value={formData.startingEquity}
          onChange={handleInputChange}
          style={inputStyle}
          min="100"
          step="100"
        />
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Quantity per Trade</label>
        <input
          type="number"
          name="allocatedAmount"
          value={formData.allocatedAmount}
          onChange={handleInputChange}
          style={inputStyle}
          min="1"
          step="1"
        />
        <small style={{ 
          display: 'block', 
          marginTop: '3px', 
          color: theme?.textSecondary || '#666', 
          fontSize: '10px',
          fontStyle: 'italic'
        }}>
          Number of shares to buy/sell per trade
        </small>
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Stop Loss (%)</label>
        <input
          type="number"
          name="stopLoss"
          value={formData.stopLoss || ''}
          onChange={handleInputChange}
          style={inputStyle}
          min="0"
          step="0.1"
          placeholder="e.g., 5.0 for 5%"
        />
      </div>

      <div style={formGroupStyle}>
        <label style={labelStyle}>Target Profit (%)</label>
        <input
          type="number"
          name="target"
          value={formData.target || ''}
          onChange={handleInputChange}
          style={inputStyle}
          min="0"
          step="0.1"
          placeholder="e.g., 10.0 for 10%"
        />
      </div>

      <button
        onClick={runBacktest}
        disabled={isRunning || !!dateError}
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: isRunning || dateError ? '#ccc' : '#4285f4',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 500,
          cursor: isRunning || dateError ? 'not-allowed' : 'pointer',
          marginTop: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!isRunning && !dateError) e.currentTarget.style.backgroundColor = '#3367d6';
        }}
        onMouseLeave={(e) => {
          if (!isRunning && !dateError) e.currentTarget.style.backgroundColor = '#4285f4';
        }}
      >
        {isRunning ? (
          <>
            <div style={spinnerStyle}></div>
            Running Backtest...
          </>
        ) : (
          <>
            ▶ Start Backtest
          </>
        )}
      </button>
    </div>
  );
};

export default BacktestForm;